import axios from 'axios';
import awsConfig from '../config';

const api = axios.create({
  baseURL: awsConfig.apiEndpoint,
  headers: {
    'Content-Type': 'application/json'
  }
});

// Add token to requests
api.interceptors.request.use(
  (config) => {
    const token = localStorage.getItem('token');
    if (token) {
      config.headers.Authorization = `Bearer ${token}`;
    }
    return config;
  },
  (error) => {
    return Promise.reject(error);
  }
);

// Handle errors
api.interceptors.response.use(
  (response) => response,
  (error) => {
    if (error.response?.status === 401) {
      // Token expired or invalid
      localStorage.removeItem('token');
      localStorage.removeItem('user');
      window.location.href = '/login';
    }
    return Promise.reject(error);
  }
);

// Auth services
export const authService = {
  register: async (userData) => {
    const response = await api.post('/auth/register', userData);
    return response.data;
  },
  
  login: async (email, password) => {
    const response = await api.post('/auth/login', { email, password });
    return response.data;
  }
};

// Invoice services
export const invoiceService = {
  listInvoices: async () => {
    const response = await api.get('/invoices');
    return response.data;
  },
  
  getInvoice: async (invoiceId) => {
    const response = await api.get(`/invoices/${invoiceId}`);
    return response.data;
  },
  
  createInvoice: async (invoiceData) => {
    const response = await api.post('/invoices', invoiceData);
    return response.data;
  },
  
  updateInvoice: async (invoiceId, invoiceData) => {
    const response = await api.put(`/invoices/${invoiceId}`, invoiceData);
    return response.data;
  },
  
  approveInvoice: async (invoiceId) => {
    const response = await api.post(`/invoices/${invoiceId}/approve`);
    return response.data;
  },
  
  rejectInvoice: async (invoiceId, comments) => {
    const response = await api.post(`/invoices/${invoiceId}/reject`, { comments });
    return response.data;
  },

  validateAfip: async (invoiceId, authCuit) => {
    const response = await api.post(`/invoices/${invoiceId}/validate-afip`, { authCuit });
    return response.data;
  }
};

// AFIP services
export const afipService = {
  getAfipCertUploadUrl: async (cuit) => {
    const response = await api.get('/afip/certificates/upload-url', { params: { cuit } });
    return response.data;
  },

  getAfipCertStatus: async (cuit) => {
    const response = await api.get('/afip/certificates/status', { params: { cuit } });
    return response.data;
  },

  uploadCertFile: async (presignedUrl, file) => {
    await fetch(presignedUrl, {
      method: 'PUT',
      body: file,
      headers: { 'Content-Type': 'application/octet-stream' },
    });
  }
};

// Master data services
export const masterDataService = {
  listMasterData: async (type, limit = 200) => {
    const params = { limit };
    if (type) params.type = type;
    const response = await api.get('/masterdata', { params });
    return response.data;
  },

  getMetadata: async () => {
    const response = await api.get('/masterdata', { params: { metadata: true } });
    return response.data;
  },

  listCompanyCuits: async () => {
    const response = await api.get('/master-data/societys/cuits');
    return response.data;
  },
  
  createMasterData: async (data) => {
    const response = await api.post('/masterdata', data);
    return response.data;
  },

  updateMasterData: async (data) => {
    const response = await api.put('/masterdata', data);
    return response.data;
  },

  deleteMasterData: async (data) => {
    const response = await api.delete('/masterdata', { data });
    return response.data;
  }
};

// User management services
export const userService = {
  listUsers: async () => {
    const response = await api.get('/users');
    return response.data;
  },
  
  createUser: async (userData) => {
    const response = await api.post('/users', userData);
    return response.data;
  },
  
  updateUser: async (userId, userData) => {
    const response = await api.put(`/users/${userId}`, userData);
    return response.data;
  },

  updateCurrentUser: async (userData) => {
    const response = await api.patch('/users/me', userData);
    return response.data;
  },
  
  deleteUser: async (userId) => {
    const response = await api.delete(`/users/${userId}`);
    return response.data;
  }
};

export default api;
